import { useEffect, useRef } from 'react';

interface PaginationFooterProps {
  hasMore: boolean;
  isLoading: boolean;
  error?: string | null;
  itemCount: number;
  loadingTitle: string;
  finishedTitle?: string;
  onLoadMore?: () => void;
  onRetry?: () => void;
}

export default function PaginationFooter({
  hasMore, isLoading, error, itemCount, loadingTitle, finishedTitle, onLoadMore, onRetry,
}: PaginationFooterProps) {
  const sentinel = useRef<HTMLDivElement>(null);
  const canLoad = hasMore && !isLoading && !error && itemCount > 0;

  useEffect(() => {
    if (!canLoad || !onLoadMore || !sentinel.current) return;
    let active = true;
    const observer = new IntersectionObserver((entries) => {
      if (!active || !entries.some((entry) => entry.isIntersecting)) return;
      active = false;
      observer.disconnect();
      onLoadMore();
    }, { rootMargin: '0px 0px 160px 0px' });
    observer.observe(sentinel.current);
    return () => { active = false; observer.disconnect(); };
  }, [canLoad, onLoadMore, itemCount]);

  return (
    <div ref={sentinel} className="min-h-[44px] py-5 text-center" aria-live="polite">
      {error ? (
        <div role="alert">
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={onRetry || onLoadMore} disabled={isLoading}
            className="mt-3 rounded-xl border border-border bg-white px-4 py-2 text-sm font-semibold text-primary">
            Retry
          </button>
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center gap-2">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-cyan" />
          <span className="text-sm text-secondary">{loadingTitle}</span>
        </div>
      ) : !hasMore && finishedTitle ? (
        <p className="text-xs font-semibold text-secondary-light">{finishedTitle}</p>
      ) : null}
    </div>
  );
}
